import { useEffect, useRef, useState } from "react";

type FetchDataFunction<T> = () => Promise<T>;

type Options = {
    frequency?: number;
    condition?: boolean;
};

const defaultOptions: Options = {
    frequency: 30000,
    condition: true,
};

const useFetchInterval = <T>(fetchData: FetchDataFunction<T>, options = defaultOptions): void => {
    const [isVisible, setIsVisible] = useState(!document.hidden);
    const wasHidden = useRef(false);

    // keep the latest fetchData in a ref so an unmemoized callback can't reset
    // the interval on every render; the effect below depends only on the options
    const fetchDataRef = useRef(fetchData);
    fetchDataRef.current = fetchData;

    const mergedOptions = {
        ...defaultOptions,
        ...options,
    };

    useEffect(() => {
        const handleVisibilityChange = () => {
            setIsVisible(!document.hidden);
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, []);

    useEffect(() => {
        let interval: NodeJS.Timeout;

        if (mergedOptions.condition && isVisible) {
            // the data may have gone stale while the tab was hidden, refresh it right away
            if (wasHidden.current) {
                void fetchDataRef.current();
            }
            interval = setInterval(async () => {
                await fetchDataRef.current();
            }, mergedOptions.frequency);
        }
        wasHidden.current = !isVisible;

        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [mergedOptions.condition, isVisible, mergedOptions.frequency]);
};

export default useFetchInterval;
